import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Loading,
  Typography,
} from "../../../components";
import {
  BancosCaixasService,
  CentroCustoService,
  SessionService,
  MonitorFinanceiroService,
  notification,
} from "../../../services";
import { responseErros } from "../../../utils";
import { buildFiltrosBody, filtrosIniciais } from "./monitor-financeiro";
import {
  MonitorFinFiltros,
  MonitorFinKpis,
  MonitorFinEntradasSaidas,
  MonitorFinFluxoPrevisto,
  MonitorFinRankingDespesas,
  MonitorFinPrazosInadimplencia,
  MonitorFinEntradasSaidasTotais,
  MonitorFinRankingInadimplencia,
  MonitorFinDre,
  MonitorFinCiclo,
} from "./components";
import { responseStreamHelper } from "../../../helpers";

const bancosCaixasService = new BancosCaixasService();
const centroCustoService = new CentroCustoService();
const sessionService = new SessionService();
const monitorFinanceiroService = new MonitorFinanceiroService();

const MonitorFinanceiro = () => {
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();
  const empresaRelatorio = sessionService.getEmpresaRelatorio();
  const acessosEmpresas = usuario?.acessosEmpresas || [];

  const filtrosPadrao = {
    ...filtrosIniciais,
    empresas: empresa ? [empresa] : [],
  };

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filtrosPadrao);
  const [filtros, setFiltros] = useState(filtrosPadrao);
  const filtrosBody = useMemo(() => buildFiltrosBody(filtros), [filtros]);
  const [listas, setListas] = useState({ contas: [], centrosCusto: [] });

  const activeCount =
    (acessosEmpresas.length > 0 ? filtros.empresas?.length || 0 : 0) +
    (filtros.contas?.length || 0) +
    (filtros.centroCusto ? 1 : 0);

  useEffect(() => {
    buscarListas();
  }, []);

  async function buscarListas() {
    const [resContas, resCentros] = await Promise.all([
      bancosCaixasService.getAll({ nonPaginated: true }),
      centroCustoService.getAll({ nonPaginated: true }),
    ]);
    if (!resContas.isAxiosError) {
      setListas((prev) => ({
        ...prev,
        contas: resContas.data?.rows || resContas.data || [],
      }));
    } else {
      responseErros(resContas);
    }
    if (!resCentros.isAxiosError) {
      setListas((prev) => ({
        ...prev,
        centrosCusto: resCentros.data?.rows || resCentros.data || [],
      }));
    }
  }

  const abrirFiltros = () => {
    setDraft(filtros);
    setOpen(true);
  };

  async function gerarPdf() {
    setLoading(true);
    try {
      const pdfBase64 = await monitorFinanceiroService.gerarPdf({
        ...filtrosBody,
        empresa: empresaRelatorio,
      });

      if (!pdfBase64.ok) {
        setLoading(false);
        responseErros(pdfBase64);
        return {};
      }
      const blob = await responseStreamHelper.getBlobPdf(pdfBase64);
      setLoading(false);
      await openAbaPdfBlob(blob);
    } catch (error) {
      setLoading(false);
      notification.erroInternoServidor();
    }
  }

  async function openAbaPdfBlob(blob) {
    try {
      const url = URL.createObjectURL(blob);
      const pdfWindow = window.open(url, "_blank");
      if (!pdfWindow) {
        notification.alertaGenericos(
          "Popup bloqueado - por favor permita popups para este site",
        );
      }

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Erro ao abrir PDF:", error);
      throw error;
    }
  }

  return (
    <Card className="minimal-layout-card">
      <CardContent className="minimal-layout-card-content rolagem">
        <Box sx={{ display: "flex", flexDirection: "column", pb: "24px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              pb: 3,
            }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Monitor Financeiro
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Acompanhe recebimentos, pagamentos, inadimplência e fluxo de
                caixa
              </Typography>
            </Box>
            <Box className="d-flex align-items-center gap-3">
              <IconButton onClick={gerarPdf} color="primary">
                <i className="ph-fill ph-printer" />
              </IconButton>
              <Badge
                badgeContent={activeCount}
                color="primary"
                overlap="rectangular"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={abrirFiltros}
                  startIcon={<i className="ph-fill ph-sliders-horizontal" />}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Filtros
                </Button>
              </Badge>
            </Box>
          </Box>

          <MonitorFinFiltros
            filtros={filtros}
            filtrosPadrao={filtrosPadrao}
            onAplicar={setFiltros}
            listas={listas}
            acessosEmpresas={acessosEmpresas}
            draft={draft}
            setDraft={setDraft}
            open={open}
            setOpen={setOpen}
          />
        </Box>

        <MonitorFinKpis filtrosBody={filtrosBody} />

        <Grid container spacing={2} alignItems="stretch" sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <MonitorFinEntradasSaidas filtrosBody={filtrosBody} />
          </Grid>
          <Grid item xs={12} md={6}>
            <MonitorFinFluxoPrevisto filtrosBody={filtrosBody} />
          </Grid>

          <Grid item xs={12} md={5}>
            <MonitorFinRankingDespesas filtrosBody={filtrosBody} />
          </Grid>
          <Grid item xs={12} md={7}>
            <MonitorFinPrazosInadimplencia filtrosBody={filtrosBody} />
          </Grid>

          <Grid item xs={12} md={6}>
            <MonitorFinEntradasSaidasTotais filtrosBody={filtrosBody} />
          </Grid>
          <Grid item xs={12} md={6}>
            <MonitorFinRankingInadimplencia filtrosBody={filtrosBody} />
          </Grid>

          <Grid item xs={12}>
            <MonitorFinDre filtrosBody={filtrosBody} />
          </Grid>

          <Grid item xs={12}>
            <MonitorFinCiclo filtrosBody={filtrosBody} />
          </Grid>
        </Grid>
      </CardContent>
      <Loading loading={loading} />
    </Card>
  );
};

export default MonitorFinanceiro;
